<script setup lang="ts">
import useISOCode from "~/composables/useISOCode";
import { useAuth } from "~/store/auth";
import { useSettings } from "~/store/settings";
import t from "~/utils/translate";

interface IBanner {
	vertical: string;
	slideCount: string;
}

const settings = useSettings();
const router = useRouter();
const info = ref(false);
const auth = useAuth();
const infoContent = ref<string | null>();
const { $trackEvent } = useNuxtApp();
const authToken = computed(() => (auth.loggedIn ? auth.token : ""));
defineProps<IBanner>();

const aspects: { [x: string]: string } = {
	"1": "aspect-[4.4/1]",
	"2": "aspect-[4.4/1] md:aspect-[3/1]",
	"3": "aspect-[4.4/1] md:aspect-[4.35/1]",
	"4": "aspect-[4.4/1] md:aspect-[6/1]",
};

interface IEvent extends Event {
	detail: string;
}

interface IBannerEvent extends Event {
	detail: {
		Action: string;
		Value: string;
	};
}

const handleMoreInfo = (e: IEvent) => {
	info.value = true;
	infoContent.value = e.detail;
	$trackEvent.bannerInfoClick();
};

const handleBannerClicked = (e: IBannerEvent) => {
	$trackEvent.bannerClick();

	const url = e.detail.Value;

	if (url.includes("/lobby/") && settings.hasNativeLobby && settings.isNativeApp) {
		return useNuxtApp().$messageHandler({
			message: "Navigate",
			data: {
				path: url,
				vertical: url.split("/")[2],
			},
		});
	}

	if (url) {
		if (url.includes("?")) {
			window.location.href = url;
		} else {
			router.push(url);
		}
	}
};

const bannerKey = ref(0);
const handleBannerResize = () => {
	bannerKey.value++;
};

onMounted(() => {
	if (window.matchMedia) window.matchMedia("(orientation: portrait)").addEventListener("change", handleBannerResize);
	if (window.matchMedia) window.matchMedia("(orientation: landscape)").addEventListener("change", handleBannerResize);
	document.addEventListener("moreInfoClicked", (e) => handleMoreInfo(e as IEvent));
	document.addEventListener("bannerClicked", (e) => handleBannerClicked(e as IBannerEvent));
});

onUnmounted(() => {
	if (window.matchMedia) window.matchMedia("(orientation: portrait)").removeEventListener("change", handleBannerResize);
	if (window.matchMedia) window.matchMedia("(orientation: landscape)").removeEventListener("change", handleBannerResize);
	document.removeEventListener("moreInfoClicked", (e) => handleMoreInfo(e as IEvent));
	document.removeEventListener("bannerClicked", (e) => handleBannerClicked(e as IBannerEvent));
});
</script>

<template>
	<div :class="[aspects[slideCount], 'relative']">
		<UiModal :title="t('more-info')" v-if="info" :active="info" @set-active="(val) => (info = val)" anchor="top">
			<div v-html="infoContent" class="text-sm leading-5" />
		</UiModal>
		<swiper-slider-v2
			v-if="auth.hydrated"
			:key="auth.loggedIn ? `banner-logged-in-${bannerKey || vertical}` : `banner-logged-out-${bannerKey || vertical}`"
			ref="banner"
			:region="settings.config?.regionCode"
			:vertical="vertical"
			channel="webdesktop"
			:auth="authToken"
			interval="5000"
			:culture="settings.locale"
			:slidecount="slideCount"
		/>
	</div>
</template>
